import ReactMarkdown from "markdown-to-jsx";
import React from "react";
import classNames from "classnames";
import Image from "components/imageNew/image";
import {
  EmailButton,
  LinkedInButton,
} from "components/socialButtons/socialButtons";
import type { TeamMemberAbout } from "content-ui/utilityPages/about/types";
import { imageLoader } from "utils";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

type TeamMemberCardProps = {
  member: TeamMemberAbout;
};

export const TeamMemberCard = ({ member }: TeamMemberCardProps) => {
  const { track } = useMixpanelTracking();

  return (
    <div className="flex gap-x-4">
      {member.image?.url && (
        <Image
          className={classNames(
            "[clip-path:_polygon(11%_0,_100%_0,_100%_20%,_100%_80%,_92%_100%,_0_100%,_0%_80%,_0_13%)]",
            "h-[90px] w-[86px] object-cover"
          )}
          loader={imageLoader}
          src={member.image.url}
          alt={`${member.name} portrait`}
          height={member.image.height}
          width={member.image.width}
          sizes="(min-width: 768px) 200px, (min-width: 1200px) 400px, 400px"
        />
      )}
      <div className="flex w-100 flex-col">
        <p className="font-bold text-[16px]">{member.name}</p>
        <p className="mt-[6px] font-bold text-[14px]">
          {member.companyPosition}
        </p>
        <div className="mt-3" data-testid={"teamMemberCard__bio"}>
          {member.bio && (
            <ReactMarkdown
              options={{ forceBlock: true }}
              className={classNames(
                "text-[14px] leading-[130%] [&_p]:m-0",
                "[&_a]:inline [&_a]:underline [&_a]:[text-underline-position:under]",
                "[&_a]:text-cerise focus-visible:[&_a]:ring-cerise",
                "focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring"
              )}
            >
              {member.bio}
            </ReactMarkdown>
          )}
        </div>

        <div className="mt-3 flex items-center gap-x-5">
          <EmailButton
            href={`mailto:${member.email}`}
            onClick={async () => {
              await track("Team member card click", {
                "Team member": member.name,
                Type: "Email",
              });
            }}
          />
          <LinkedInButton
            href={member.linkedin}
            onClick={async () => {
              await track("Team member card click", {
                "Team member": member.name,
                Type: "LinkedIn",
                "Click link": member.linkedin,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
